import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

const ORANGE = "#f26b2b";
const INK = "#171717";

/**
 * Soft ambient 3D field — particles + gentle rings.
 * Designed as background atmosphere, not a gimmick object.
 */
function AmbientField({ variant = "hero" }) {
  const group = useRef();
  const ringA = useRef();
  const ringB = useRef();

  const positions = useMemo(() => {
    const count = variant === "hero" ? 360 : 220;
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      const radius = variant === "hero" ? 1.8 + Math.random() * 3.4 : 1.4 + Math.random() * 2.4;
      const angle = Math.random() * Math.PI * 2;
      arr[i * 3] = Math.cos(angle) * radius;
      arr[i * 3 + 1] = (Math.random() - 0.5) * (variant === "hero" ? 2.6 : 1.8);
      arr[i * 3 + 2] = Math.sin(angle) * radius * 0.65;
    }
    return arr;
  }, [variant]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (group.current) group.current.rotation.y = t * 0.04;
    if (ringA.current) {
      ringA.current.rotation.z = t * 0.18;
      ringA.current.rotation.x = 0.7 + Math.sin(t * 0.2) * 0.05;
    }
    if (ringB.current) {
      ringB.current.rotation.z = -t * 0.12;
      ringB.current.rotation.x = -0.5 + Math.cos(t * 0.15) * 0.04;
    }
  });

  return (
    <group ref={group}>
      <points>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        </bufferGeometry>
        <pointsMaterial
          size={variant === "hero" ? 0.028 : 0.024}
          color={ORANGE}
          transparent
          opacity={variant === "hero" ? 0.35 : 0.55}
          depthWrite={false}
          sizeAttenuation
        />
      </points>

      <mesh ref={ringA}>
        <torusGeometry args={[variant === "hero" ? 2.2 : 1.55, 0.012, 16, 160]} />
        <meshBasicMaterial color={ORANGE} transparent opacity={variant === "hero" ? 0.22 : 0.35} />
      </mesh>
      <mesh ref={ringB}>
        <torusGeometry args={[variant === "hero" ? 2.8 : 2.0, 0.008, 16, 160]} />
        <meshBasicMaterial color={INK} transparent opacity={variant === "hero" ? 0.14 : 0.22} />
      </mesh>

      {variant !== "hero" ? (
        <mesh>
          <sphereGeometry args={[0.4, 32, 32]} />
          <meshStandardMaterial
            color={INK}
            emissive={ORANGE}
            emissiveIntensity={0.12}
            metalness={0.35}
            roughness={0.4}
            transparent
            opacity={0.9}
          />
        </mesh>
      ) : null}
    </group>
  );
}

export default function AmbientCanvas({
  variant = "hero",
  className = "",
  cameraZ = 6,
}) {
  return (
    <div className={`lp-canvas ${className}`.trim()} aria-hidden="true">
      <Canvas
        camera={{ position: [0, 0.1, cameraZ], fov: 42 }}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true, premultipliedAlpha: false }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0);
        }}
      >
        <Suspense fallback={null}>
          <ambientLight intensity={0.75} />
          <directionalLight position={[4, 5, 3]} intensity={0.9} color="#fff6ee" />
          <pointLight position={[-3, 1, 2]} intensity={0.45} color={ORANGE} />
          <AmbientField variant={variant} />
        </Suspense>
      </Canvas>
    </div>
  );
}
